import { motion } from 'motion/react';
import { type ButtonHTMLAttributes, type MouseEvent, type ReactNode, forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router';
import { useAudio } from '../context/AudioContext';
import { cn } from '../lib/cn';

/**
 * Signature CTA — TWO variants, two roles:
 *
 *  - `primary` (default): editorial text in display font + stroke-circle on the
 *    right with arrow inside. On hover the circle fills, the arrow flips colour
 *    and translates. Use ONLY for the single most important action per
 *    viewport (hero CTA, final CTA, booking modal submit). Max 1 per viewport.
 *
 *  - `link`: text only with an underline that grows from left-to-right on
 *    hover. Display font lowercase. Use for everything else: "view all",
 *    "back to artists", footer links, Instagram, lightbox CTAs, etc.
 *
 * Both variants set `data-magnetic="true"` so the canvas cursor reacts.
 */

type Variant = 'primary' | 'link';
type Tone = 'light' | 'dark';
type Size = 'md' | 'lg';

type CommonProps = {
  children: ReactNode;
  variant?: Variant;
  tone?: Tone;
  size?: Size;
  className?: string;
  /** Glyph shown inside the primary circle. Default "→" */
  suffix?: string;
};

type ButtonProps = CommonProps & { as?: 'button' } & Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children'
  >;
type LinkAnchorProps = CommonProps & {
  as: 'link';
  to: LinkProps['to'];
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};
type ExternalProps = CommonProps & {
  as: 'a';
  href: string;
  target?: string;
  rel?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

type Props = ButtonProps | LinkAnchorProps | ExternalProps;

const SIZE: Record<
  Size,
  { circle: number; circleMobile: number; gap: string; text: string; linkText: string }
> = {
  md: {
    circle: 38,
    circleMobile: 34,
    gap: 'gap-3 md:gap-3.5',
    text: 'text-lg md:text-2xl',
    linkText: 'text-base md:text-lg',
  },
  lg: {
    circle: 56,
    circleMobile: 44,
    gap: 'gap-3 md:gap-5',
    text: 'text-2xl md:text-4xl',
    linkText: 'text-xl md:text-3xl',
  },
};

export const InkButton = forwardRef<HTMLElement, Props>(function InkButton(props, ref) {
  const {
    children,
    variant = 'primary',
    tone = 'light',
    size = 'lg',
    className,
    suffix = '→',
  } = props;
  const dims = SIZE[size];
  const audio = useAudio();

  const fg = tone === 'dark' ? 'var(--color-ink-base)' : 'var(--color-ink-paper)';
  const bg = tone === 'dark' ? 'var(--color-ink-paper)' : 'var(--color-ink-base)';

  const primaryInner = (
    <span
      className={cn(
        'inline-flex items-center select-none whitespace-nowrap',
        dims.gap,
        className,
      )}
      style={{ color: fg }}
    >
      <span
        className={cn(
          'font-display tracking-display leading-none lowercase',
          dims.text,
          'block transition-transform duration-500 ease-out group-hover/btn:translate-x-1',
        )}
      >
        {children}
      </span>

      <span
        aria-hidden
        className="relative shrink-0 size-[var(--ink-circle-sm)] md:size-[var(--ink-circle-md)]"
        style={
          {
            '--ink-circle-sm': `${dims.circleMobile}px`,
            '--ink-circle-md': `${dims.circle}px`,
          } as React.CSSProperties
        }
      >
        <svg
          viewBox={`0 0 ${dims.circle} ${dims.circle}`}
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 h-full w-full overflow-visible"
        >
          <circle
            cx={dims.circle / 2}
            cy={dims.circle / 2}
            r={dims.circle / 2 - 1}
            fill="none"
            stroke={fg}
            strokeWidth={1}
          />
          <motion.circle
            cx={dims.circle / 2}
            cy={dims.circle / 2}
            r={dims.circle / 2 - 1}
            fill={fg}
            variants={{ rest: { scale: 0 }, hover: { scale: 1 } }}
            style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>
        <motion.span
          className={cn(
            'absolute inset-0 flex items-center justify-center leading-none',
            size === 'lg' ? 'text-base md:text-xl' : 'text-sm md:text-base',
          )}
          variants={{
            rest: { color: fg, x: 0 },
            hover: { color: bg, x: 2 },
          }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          {suffix}
        </motion.span>
      </span>
    </span>
  );

  const linkInner = (
    <span
      className={cn(
        'relative inline-flex items-baseline select-none whitespace-nowrap pb-1',
        className,
      )}
      style={{ color: fg }}
    >
      <span
        className={cn(
          'font-display tracking-display leading-none lowercase',
          dims.linkText,
        )}
      >
        {children}
      </span>
      <span
        className={cn(
          'font-display tracking-display leading-none lowercase ml-1.5 transition-transform duration-500 ease-out group-hover/btn:translate-x-1',
          dims.linkText,
        )}
        aria-hidden
      >
        {suffix}
      </span>
      {/* Underline that propagates from left on hover */}
      <motion.span
        aria-hidden
        className="absolute left-0 bottom-0 h-px origin-left"
        style={{ backgroundColor: fg, width: '100%' }}
        variants={{
          rest: { scaleX: 0 },
          hover: { scaleX: 1 },
        }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      />
    </span>
  );

  const motionWrap = (
    <motion.span
      className="group/btn inline-flex"
      initial="rest"
      animate="rest"
      whileHover="hover"
      whileFocus="hover"
    >
      {variant === 'primary' ? primaryInner : linkInner}
    </motion.span>
  );

  if (props.as === 'link') {
    const { to, onClick } = props;
    return (
      <Link
        to={to}
        onClick={(e) => {
          audio.play('click');
          onClick?.(e);
        }}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className="inline-flex"
        data-magnetic="true"
      >
        {motionWrap}
      </Link>
    );
  }

  if (props.as === 'a') {
    const { href, target, rel, onClick } = props;
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={(e) => {
          audio.play('click');
          onClick?.(e);
        }}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className="inline-flex"
        data-magnetic="true"
      >
        {motionWrap}
      </a>
    );
  }

  const { onClick, type, disabled } = props;
  return (
    <button
      type={type ?? 'button'}
      onClick={(e) => {
        audio.play('click');
        onClick?.(e);
      }}
      disabled={disabled}
      ref={ref as React.Ref<HTMLButtonElement>}
      className="inline-flex"
      data-magnetic="true"
    >
      {motionWrap}
    </button>
  );
});
